import type { TokenRequest } from "ably"
import { Mutex } from "async-mutex"
import type {
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react"
import { createApi, retry } from "@reduxjs/toolkit/query/react"
import fetchBaseQuery from "./fetchBaseQuery"
import { clearAuth, updateTokens } from "@/auth/slice"
import type { TokenResponse } from "@/auth/model"
import { getRefreshToken } from "@/auth/refreshTokenStorage"
import { withRefreshLock } from "@/auth/refreshLock"
import { isRefreshTokenRejected } from "@/auth/refreshFailure"

export type BaseQueryExtraOptions = {
  readonly withoutAuth?: boolean
}

type Assert = (condition: boolean) => asserts condition

const getResponseCode = (
  error: FetchBaseQueryError | undefined,
): number | undefined => {
  if (!error) {
    return undefined
  }

  const status = error.status

  if (status === "PARSING_ERROR") {
    return error.originalStatus
  }

  if (typeof status != "number") {
    return undefined
  }

  return status
}

const mutex = new Mutex()

/**
 * Endpoints the refresh path must never touch, whatever they carry.
 *
 * The refresh itself, or a dead token would spawn refreshes of refreshes. And signing out, which
 * is not something to interrupt with a renewal of the session being ended.
 *
 * Both also matter for liveness: each runs while the cross-tab refresh lock is held, so waiting on
 * the in-tab mutex or reaching for that lock from here would be a circular wait — the holder of one
 * waiting on the other, and neither able to settle.
 */
const neverRefreshes = (endpoint: string) =>
  endpoint === "tokenRefresh" || endpoint === "logout"

const baseQuery = retry(
  async (
    args: string | FetchArgs,
    api,
    extraOptions?: BaseQueryExtraOptions,
  ) => {
    const { dispatch, endpoint } = api
    // A request carrying no credentials cannot have expired ones either: its 401 means what it
    // sent was wrong, and refreshing over that would sign a signed-in user out.
    const isolated = neverRefreshes(endpoint) || !!extraOptions?.withoutAuth

    const baseQuery = fetchBaseQuery({
      withoutAuth: !!extraOptions?.withoutAuth,
    })

    if (!isolated) {
      // Wait for a possible concurrent token refresh
      await mutex.waitForUnlock()
    }

    const result = await baseQuery(args, api, extraOptions ?? {})

    // 401 suggests token has expired, so we try to fetch a new one – or clear auth state if it fails
    // If mutex is locked then another request is already refreshing so we let this one fail and go for retry
    // This be tightly coupled with retry options below – backoff and retryCondition
    if (
      getResponseCode(result.error) === 401 &&
      !mutex.isLocked() &&
      !isolated
    ) {
      // Lock and attempt token refresh
      const release = await mutex.acquire()
      try {
        // The token this session was using, so clearAuth cannot delete a newer one another tab
        // stored while this request was in flight.
        const giveUp = (usedToken?: string): never => {
          dispatch(clearAuth(usedToken))
          retry.fail(result.error)
        }

        const assert: Assert = (condition) => {
          if (!condition) {
            giveUp()
          }
        }

        // The mutex above only covers this tab; the token rotates on every use, so the exchange
        // itself has to be serialised across tabs too. Read inside the lock, never before it.
        await withRefreshLock(async () => {
          const refreshToken = getRefreshToken()
          assert(!!refreshToken)

          try {
            // Refresh token
            await dispatch(
              baseApi.endpoints.tokenRefresh.initiate(refreshToken),
            ).unwrap()
          } catch (error) {
            // Only an outright rejection ends the session. A rate limit or a dropped connection
            // leaves the stored token alone: this request fails, the next one refreshes again.
            if (isRefreshTokenRejected(error)) {
              giveUp(refreshToken)
            }

            retry.fail(result.error)
          }
        })
      } finally {
        release()
      }
    }

    return result
  },
  {
    backoff: async (attempt, maxRetries) => {
      if (attempt === 1) {
        // No waiting for first retry to allow instant recovery after token refresh
        return
      }

      // Below is the defaultBackoff copied from RTK as it is not exported

      const attempts = Math.min(attempt, maxRetries) - 1

      const timeout = (Math.random() + 0.4) * (300 << attempts)
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          resolve()
        }, timeout),
      )
    },
    retryCondition: (error, _args, { attempt, baseQueryApi }) => {
      // bail out of re-tries for client-side errors like validation
      // Except unauth where we rely on automatic refresh and retry — which a request that never
      // goes through it cannot be rescued by, so repeating that one only delays the failure. That
      // is what keeps the refresh exchange from replaying a spent token against its own 401.
      const responseCode = getResponseCode(
        error as FetchBaseQueryError | undefined,
      )

      const shouldRetry =
        !responseCode ||
        (responseCode === 401 && !neverRefreshes(baseQueryApi.endpoint)) ||
        responseCode < 400 ||
        responseCode >= 500

      return shouldRetry && attempt < 3
    },
  },
)

const baseApi = createApi({
  baseQuery,
  refetchOnMountOrArgChange: 60,
  refetchOnReconnect: true,
  endpoints: (build) => ({
    tokenRefresh: build.mutation<TokenResponse, string>({
      query: (payload) => ({
        url: "security/refresh",
        method: "POST",
        body: { refreshToken: payload },
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(updateTokens(data))
        } catch {
          // noop
        }
      },
    }),
    ablyAuth: build.mutation<TokenRequest, void>({
      query: () => ({
        url: "ably-auth",
        method: "POST",
      }),
    }),
  }),
})

export type BaseQuery = typeof baseQuery

export const { useTokenRefreshMutation, useAblyAuthMutation } = baseApi

export default baseApi
